import { useState, useEffect } from 'react';
import { toastListeners } from '../lib/notify.js';

// Shows notify() messages just above the bottom tab bar. Each one closes itself
// after its duration, on tap, or when its action (e.g. Undo) is used. At most
// three at once — older ones drop off.
export function Toaster({ darkMode }) {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    const timers = new Map();
    const dismiss = (id) => {
      clearTimeout(timers.get(id));
      timers.delete(id);
      setToasts(ts => ts.filter(t => t.id !== id));
    };
    const onToast = (t) => {
      const toast = { ...t, dismiss: () => dismiss(t.id) };
      setToasts(ts => [...ts.slice(-2), toast]);
      timers.set(t.id, setTimeout(() => dismiss(t.id), t.duration));
    };
    toastListeners.add(onToast);
    return () => { toastListeners.delete(onToast); timers.forEach(clearTimeout); };
  }, []);

  if (toasts.length === 0) return null;
  return (
    <div className="fixed inset-x-0 z-50 px-3 flex flex-col items-center gap-2 pointer-events-none"
      style={{ bottom: 'calc(env(safe-area-inset-bottom) + 72px)' }}
      role="status" aria-live="polite">
      {toasts.map(t => (
        <div key={t.id} onClick={t.dismiss}
          className={`pointer-events-auto w-full max-w-md flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm ${
            t.tone === 'error'
              ? (darkMode ? 'bg-red-900 text-red-100 border border-red-700' : 'bg-red-50 text-red-800 border border-red-300')
              : (darkMode ? 'bg-gray-700 text-gray-100 border border-gray-600' : 'bg-gray-800 text-white')
          }`}>
          <span className="flex-1 min-w-0">{t.message}</span>
          {t.action && (
            <button type="button"
              onClick={(e) => { e.stopPropagation(); t.action.onClick(); t.dismiss(); }}
              className={`flex-shrink-0 font-semibold uppercase tracking-wide text-xs px-2 py-1 rounded ${
                darkMode ? 'text-indigo-300 hover:bg-gray-600' : 'text-indigo-300 hover:bg-gray-700'
              }`}>
              {t.action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
